import React, { useMemo } from "react";
import { Card, Table, Tooltip, Button, Tag } from "antd";
import dayjs from "dayjs";
import {
    DesktopOutlined,
    MobileOutlined,
    FileExcelOutlined
} from "@ant-design/icons";
import { exportToExcel } from "./exportToExcelModule";

const DeviceUsageDetails = ({ deviceUsageData = [], client, darkTheme, style = {} }) => {
    const columns = useMemo(() => [
        {
            title: "Mnemonic",
            dataIndex: "event_mnemonic",
            key: "event_mnemonic",
            render: (text, record) => (
                <Tooltip title={record.event_name}>
                    <span>{text}</span>
                </Tooltip>
            ),
        },
        {
            title: "Begin Date",
            dataIndex: "event_beginDate",
            key: "event_beginDate",
            width: 120,
            render: (date) => dayjs(date).format("DD-MM-YYYY"),
        },
        {
            title: "End Date",
            dataIndex: "event_endDate",
            key: "event_endDate",
            width: 120,
            render: (date) => dayjs(date).format("DD-MM-YYYY"),
        },
        {
            title: "Total Devices",
            dataIndex: "event_totalDevices",
            key: "event_totalDevices",
            align: "center",
            render: (text) => (
                <strong style={{ color: darkTheme ? "limegreen" : "darkgreen", fontSize: "1.1rem" }}>
                    {text}
                </strong>
            ),
        },
        {
            title: "Total Billable",
            dataIndex: "total_billable_days",
            key: "total_billable_days",
            align: "center",
            render: (value) => (
                <strong style={{ color: darkTheme ? "limegreen" : "darkgreen", fontSize: "1.1rem" }}>
                    {value}
                </strong>
            )
        },
        {
            title: "Export",
            key: "export",
            align: "center",
            render: (_, record) => (
                <Button
                    icon={<FileExcelOutlined style={{ color: "limegreen" }} />}
                    onClick={() => exportToExcel(record, client)}
                />
            ),
        },
    ], [client, darkTheme]);

    const expandedRowRender = (record) => {
        const flattenedData = record.devices.flatMap((device) => {
            return device.configs?.length > 0
                ? device.configs.map((config, configIndex) => ({
                    key: `${device.deviceID}-${configIndex}`,
                    deviceID: device.deviceID,
                    serialNumber: device.serialNumber,
                    name: device.name,
                    type: device.type,
                    configName: config.name,
                    usedOn: config.usedOn,
                    configDaysAssigned: config.billable,
                    rowSpan: configIndex === 0 ? device.configs.length : 0,
                }))
                : [{
                    key: `${device.deviceID}-no-config`,
                    deviceID: device.deviceID,
                    serialNumber: device.serialNumber,
                    name: device.name,
                    type: device.type,
                    configName: "-",
                    usedOn: "-",
                    configDaysAssigned: "-",
                    rowSpan: 1,
                }];
        });

        const expandedColumns = [
            {
                title: "Device Name",
                dataIndex: "name",
                key: "name",
                width: 200,
                render: (text, { rowSpan, serialNumber }) =>
                    rowSpan
                        ? {
                            children: (
                                <Tooltip title={`Serial Number: ${serialNumber}`}>
                                    <span>{text}</span>
                                </Tooltip>
                            ),
                            props: { rowSpan },
                        }
                        : { children: null, props: { rowSpan } },
            },
            {
                title: "Type",
                dataIndex: "type",
                key: "type",
                width: 120,
                align: "center",
                render: (type, { rowSpan }) => {
                    const icon = type === "mobile"
                        ? <MobileOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
                        : <DesktopOutlined style={{ color: "#0078D6", fontSize: "18px" }} />;

                    return rowSpan
                        ? { children: icon, props: { rowSpan } }
                        : { children: null, props: { rowSpan } };
                },
            },
            {
                title: "Config Name",
                dataIndex: "configName",
                key: "configName",
                width: 220,
            },
            {
                title: "Used on",
                dataIndex: "usedOn",
                key: "usedOn",
                width: 110,
                render: (date) => date !== "-" ? dayjs(date).format("DD-MM-YYYY") : "-",
            },
            {
                title: "Days Assigned",
                dataIndex: "configDaysAssigned",
                key: "configDaysAssigned",
                width: 150,
                render: (value) => {
                    const val = typeof value === "string" ? value.toLowerCase() : value;

                    if (val === "not billable" || val === 0) {
                        return <Tag color="red">NOT BILLABLE</Tag>;
                    }

                    return <Tag color="green">BILLABLE</Tag>;
                }
            }
        ];

        return (
            <Table
                bordered
                columns={expandedColumns}
                dataSource={flattenedData}
                pagination={false}
                scroll={{ x: "max-content" }}
            />
        );
    };

    const transformedData = useMemo(() => (
        deviceUsageData.map((event) => ({
            key: event.eventID,
            event_mnemonic: event.event_mnemonic,
            event_name: event.event_name,
            event_beginDate: event.event_beginDate,
            event_endDate: event.event_endDate,
            event_totalDevices: event.devices.length,
            total_billable_days: event.total_billable_days,
            devices: event.devices.map((device) => ({
                ...device,
                key: device.deviceID
            }))
        }))
    ), [deviceUsageData]);

    return (
        <Card title="Event Details" style={style}>
            <Table
                columns={columns}
                dataSource={transformedData}
                rowKey="key"
                expandable={{ expandedRowRender }}
                scroll={{ x: "max-content" }}
                bordered
            />
        </Card>
    );
};

export default DeviceUsageDetails;